import { useEffect, useState, useRef } from 'react'
import s from './styles.module.scss'
import { head } from 'helpers'
import { Alert, Layouts, Loader, Input, Form } from 'components'
import * as api from 'api'

export default function Main() {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [settings, setSettings] = useState(null)
  const [stats, setStats] = useState(null)
  const processing = useRef(false)

  // Facebook settings
  const [fbEnabled, setFbEnabled] = useState(false)
  const [fbPageId, setFbPageId] = useState('')
  const [fbAccessToken, setFbAccessToken] = useState('')
  const [fbPageName, setFbPageName] = useState('')

  // Instagram settings
  const [igEnabled, setIgEnabled] = useState(false)
  const [igAccountId, setIgAccountId] = useState('')
  const [igAccessToken, setIgAccessToken] = useState('')
  const [igUsername, setIgUsername] = useState('')

  // YouTube settings
  const [ytEnabled, setYtEnabled] = useState(false)
  const [ytChannelId, setYtChannelId] = useState('')
  const [ytApiKey, setYtApiKey] = useState('')
  const [ytChannelName, setYtChannelName] = useState('')

  // WhatsApp settings
  const [waEnabled, setWaEnabled] = useState(false)
  const [waChannelUrl, setWaChannelUrl] = useState('')
  const [waChannelName, setWaChannelName] = useState('')

  // General settings
  const [fetchFrequency, setFetchFrequency] = useState(6)
  const [maxPostsPerPlatform, setMaxPostsPerPlatform] = useState(10)

  useEffect(() => {
    head({ title: 'Social Media Settings | BCN Admin' })
    fetchSettings()
    fetchStats()
  }, [])

  const fetchSettings = async () => {
    if (processing.current) return
    processing.current = true
    setLoading(true)

    try {
      const res = await api.admin.socialMediaSettings.get()
      if (res.code === 200 && res.payload) {
        const s = res.payload
        setSettings(s)

        // Facebook
        setFbEnabled(s.facebook?.enabled || false)
        setFbPageId(s.facebook?.pageId || '')
        setFbAccessToken(s.facebook?.accessToken || '')
        setFbPageName(s.facebook?.pageName || '')

        // Instagram
        setIgEnabled(s.instagram?.enabled || false)
        setIgAccountId(s.instagram?.accountId || '')
        setIgAccessToken(s.instagram?.accessToken || '')
        setIgUsername(s.instagram?.username || '')

        // YouTube
        setYtEnabled(s.youtube?.enabled || false)
        setYtChannelId(s.youtube?.channelId || '')
        setYtApiKey(s.youtube?.apiKey || '')
        setYtChannelName(s.youtube?.channelName || '')

        // WhatsApp
        setWaEnabled(s.whatsapp?.enabled || false)
        setWaChannelUrl(s.whatsapp?.channelUrl || '')
        setWaChannelName(s.whatsapp?.channelName || '')

        // General
        setFetchFrequency(s.fetchFrequency || 6)
        setMaxPostsPerPlatform(s.maxPostsPerPlatform || 10)
      } else {
        Alert.error(res.message || 'Failed to load settings')
      }
    } catch (error) {
      console.error('Error fetching settings:', error)
      Alert.error('Failed to load settings')
    }

    setLoading(false)
    processing.current = false
  }

  const fetchStats = async () => {
    try {
      const res = await api.admin.socialMediaSettings.getStats()
      if (res.code === 200 && res.payload) {
        setStats(res.payload)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const handleSave = async () => {
    if (processing.current || saving) return
    processing.current = true
    setSaving(true)

    try {
      const updateData = {
        facebook: {
          enabled: fbEnabled,
          pageId: fbPageId.trim(),
          accessToken: fbAccessToken.trim(),
          pageName: fbPageName.trim()
        },
        instagram: {
          enabled: igEnabled,
          accountId: igAccountId.trim(),
          accessToken: igAccessToken.trim(),
          username: igUsername.trim()
        },
        youtube: {
          enabled: ytEnabled,
          channelId: ytChannelId.trim(),
          apiKey: ytApiKey.trim(),
          channelName: ytChannelName.trim()
        },
        whatsapp: {
          enabled: waEnabled,
          channelUrl: waChannelUrl.trim(),
          channelName: waChannelName.trim()
        },
        fetchFrequency: Number(fetchFrequency),
        maxPostsPerPlatform: Number(maxPostsPerPlatform)
      }

      const res = await api.admin.socialMediaSettings.update(updateData)
      if (res.code === 200) {
        Alert.success('Settings saved successfully')
        fetchSettings()
        fetchStats()
      } else {
        Alert.error(res.message || 'Failed to save settings')
      }
    } catch (error) {
      console.error('Error saving settings:', error)
      Alert.error('Failed to save settings')
    }

    setSaving(false)
    processing.current = false
  }

  const handleTriggerFetch = async () => {
    if (processing.current || fetching) return
    processing.current = true
    setFetching(true)

    try {
      const res = await api.admin.socialMediaSettings.triggerFetch()
      if (res.code === 200) {
        Alert.success(
          `Fetch completed! Fetched: ${res.payload?.fetched || 0}, Saved: ${res.payload?.saved || 0}, Duplicates: ${res.payload?.duplicates || 0}`
        )
        fetchStats()
      } else {
        Alert.error(res.message || 'Failed to fetch social media posts')
      }
    } catch (error) {
      console.error('Error triggering fetch:', error)
      Alert.error('Failed to fetch social media posts')
    }

    setFetching(false)
    processing.current = false
  }

  if (loading) {
    return (
      <div className={s.main}>
        <Loader />
      </div>
    )
  }

  return (
    <div className={s.main}>
      <Layouts.Classic title='Social Media Feed Settings' />
      <div className={s.content}>
        {/* Statistics Card */}
        {stats && (
          <div className={s.statsCard}>
            <h3 className={s.statsTitle}>Statistics</h3>
            <div className={s.statsGrid}>
              <div className={s.statItem}>
                <div className={s.statLabel}>Facebook Posts</div>
                <div className={s.statValue}>{stats.facebook || 0}</div>
              </div>
              <div className={s.statItem}>
                <div className={s.statLabel}>Instagram Posts</div>
                <div className={s.statValue}>{stats.instagram || 0}</div>
              </div>
              <div className={s.statItem}>
                <div className={s.statLabel}>YouTube Videos</div>
                <div className={s.statValue}>{stats.youtube || 0}</div>
              </div>
              <div className={s.statItem}>
                <div className={s.statLabel}>WhatsApp Updates</div>
                <div className={s.statValue}>{stats.whatsapp || 0}</div>
              </div>
              <div className={s.statItem}>
                <div className={s.statLabel}>Last Fetch</div>
                <div className={s.statValue}>
                  {stats.lastFetchedAt
                    ? new Date(stats.lastFetchedAt).toLocaleString()
                    : 'Never'}
                </div>
              </div>
              <div className={s.statItem}>
                <div className={s.statLabel}>Status</div>
                <div className={s.statValue}>
                  <span
                    className={
                      stats.lastFetchStatus === 'success'
                        ? s.statusSuccess
                        : stats.lastFetchStatus === 'failed'
                        ? s.statusFailed
                        : s.statusPending
                    }
                  >
                    {stats.lastFetchStatus || 'pending'}
                  </span>
                </div>
              </div>
            </div>
            {stats.lastFetchError && (
              <div className={s.errorMessage}>Error: {stats.lastFetchError}</div>
            )}
          </div>
        )}

        {/* Settings Form */}
        <div className={s.formCard}>
          <h3 className={s.formTitle}>Platform Configuration</h3>

          <Form className={s.form}>
            {/* Facebook Section */}
            <div className={s.platformSection}>
              <div className={s.platformHeader}>
                <h4>Facebook</h4>
                <label className={s.checkboxLabel}>
                  <input
                    type='checkbox'
                    checked={fbEnabled}
                    onChange={e => setFbEnabled(e.target.checked)}
                  />
                  <span>Enable</span>
                </label>
              </div>
              {fbEnabled && (
                <div className={s.platformFields}>
                  <div className={s.formGroup}>
                    <label>Page ID</label>
                    <Input.Classic
                      type='text'
                      value={fbPageId}
                      onChange={e => setFbPageId(e.target.value)}
                      placeholder='Your Facebook Page ID'
                    />
                    <small>Find your Page ID in Page Settings → Page Info</small>
                  </div>
                  <div className={s.formGroup}>
                    <label>Access Token</label>
                    <Input.Classic
                      type='password'
                      value={fbAccessToken}
                      onChange={e => setFbAccessToken(e.target.value)}
                      placeholder='Facebook Page Access Token'
                    />
                    <small>Get from Facebook Developers → Graph API Explorer</small>
                  </div>
                  <div className={s.formGroup}>
                    <label>Page Name (Optional)</label>
                    <Input.Classic
                      type='text'
                      value={fbPageName}
                      onChange={e => setFbPageName(e.target.value)}
                      placeholder='Display name for your page'
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Instagram Section */}
            <div className={s.platformSection}>
              <div className={s.platformHeader}>
                <h4>Instagram</h4>
                <label className={s.checkboxLabel}>
                  <input
                    type='checkbox'
                    checked={igEnabled}
                    onChange={e => setIgEnabled(e.target.checked)}
                  />
                  <span>Enable</span>
                </label>
              </div>
              {igEnabled && (
                <div className={s.platformFields}>
                  <div className={s.formGroup}>
                    <label>Account ID</label>
                    <Input.Classic
                      type='text'
                      value={igAccountId}
                      onChange={e => setIgAccountId(e.target.value)}
                      placeholder='Instagram Business Account ID'
                    />
                    <small>Get from Facebook Developers → Instagram Basic Display</small>
                  </div>
                  <div className={s.formGroup}>
                    <label>Access Token</label>
                    <Input.Classic
                      type='password'
                      value={igAccessToken}
                      onChange={e => setIgAccessToken(e.target.value)}
                      placeholder='Instagram Access Token'
                    />
                    <small>Generate from Facebook Developers → Instagram Basic Display</small>
                  </div>
                  <div className={s.formGroup}>
                    <label>Username (Optional)</label>
                    <Input.Classic
                      type='text'
                      value={igUsername}
                      onChange={e => setIgUsername(e.target.value)}
                      placeholder='@username'
                    />
                  </div>
                </div>
              )}
            </div>

            {/* YouTube Section */}
            <div className={s.platformSection}>
              <div className={s.platformHeader}>
                <h4>YouTube</h4>
                <label className={s.checkboxLabel}>
                  <input
                    type='checkbox'
                    checked={ytEnabled}
                    onChange={e => setYtEnabled(e.target.checked)}
                  />
                  <span>Enable</span>
                </label>
              </div>
              {ytEnabled && (
                <div className={s.platformFields}>
                  <div className={s.formGroup}>
                    <label>Channel ID</label>
                    <Input.Classic
                      type='text'
                      value={ytChannelId}
                      onChange={e => setYtChannelId(e.target.value)}
                      placeholder='YouTube Channel ID'
                    />
                    <small>Find in Channel Settings → Advanced Settings</small>
                  </div>
                  <div className={s.formGroup}>
                    <label>API Key</label>
                    <Input.Classic
                      type='password'
                      value={ytApiKey}
                      onChange={e => setYtApiKey(e.target.value)}
                      placeholder='YouTube Data API v3 Key'
                    />
                    <small>Get from Google Cloud Console → APIs & Services → Credentials</small>
                  </div>
                  <div className={s.formGroup}>
                    <label>Channel Name (Optional)</label>
                    <Input.Classic
                      type='text'
                      value={ytChannelName}
                      onChange={e => setYtChannelName(e.target.value)}
                      placeholder='Display name for your channel'
                    />
                  </div>
                </div>
              )}
            </div>

            {/* WhatsApp Section */}
            <div className={s.platformSection}>
              <div className={s.platformHeader}>
                <h4>WhatsApp</h4>
                <label className={s.checkboxLabel}>
                  <input
                    type='checkbox'
                    checked={waEnabled}
                    onChange={e => setWaEnabled(e.target.checked)}
                  />
                  <span>Enable</span>
                </label>
              </div>
              {waEnabled && (
                <div className={s.platformFields}>
                  <div className={s.formGroup}>
                    <label>Channel URL / RSS Feed</label>
                    <Input.Classic
                      type='text'
                      value={waChannelUrl}
                      onChange={e => setWaChannelUrl(e.target.value)}
                      placeholder='WhatsApp Channel RSS Feed URL (if available)'
                    />
                    <small>Note: WhatsApp doesn't have a public API. Use RSS feed if available.</small>
                  </div>
                  <div className={s.formGroup}>
                    <label>Channel Name (Optional)</label>
                    <Input.Classic
                      type='text'
                      value={waChannelName}
                      onChange={e => setWaChannelName(e.target.value)}
                      placeholder='Display name for your channel'
                    />
                  </div>
                </div>
              )}
            </div>

            {/* General Settings */}
            <div className={s.platformSection}>
              <h4>General Settings</h4>
              <div className={s.platformFields}>
                <div className={s.formGroup}>
                  <label>Fetch Frequency (hours)</label>
                  <Input.Classic
                    type='number'
                    value={fetchFrequency}
                    onChange={e => setFetchFrequency(e.target.value)}
                    min={1}
                    max={168}
                  />
                  <small>How often to automatically fetch new posts (1-168 hours)</small>
                </div>
                <div className={s.formGroup}>
                  <label>Max Posts Per Platform</label>
                  <Input.Classic
                    type='number'
                    value={maxPostsPerPlatform}
                    onChange={e => setMaxPostsPerPlatform(e.target.value)}
                    min={1}
                    max={50}
                  />
                  <small>Maximum number of posts to fetch per platform per run</small>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={s.formActions}>
              <button
                type='button'
                onClick={handleSave}
                disabled={saving}
                className={s.saveButton}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
              <button
                type='button'
                onClick={handleTriggerFetch}
                disabled={fetching}
                className={s.fetchButton}
              >
                {fetching ? 'Fetching...' : 'Fetch Posts Now'}
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

